(() => {
  if (customElements.get('social-videos-item')) return;

  class SocialVideosItem extends HTMLElement {
    constructor() {
      super();
      this.onMediaClick = this.onMediaClick.bind(this);
      this.onPlayClick = this.onPlayClick.bind(this);
      this.onVideoClick = this.onVideoClick.bind(this);
      this.onVideoPlaying = this.onVideoPlaying.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.onMediaPointerEnter = this.onMediaPointerEnter.bind(this);
      this.isVideoLoaded = false;
      this.preloadPromise = null;
    }

    connectedCallback() {
      this.video = this.querySelector('[data-video]');
      this.media = this.querySelector('[data-media]');
      this.poster = this.querySelector('[data-poster]');
      this.playButton = this.querySelector('[data-play]');

      if (!this.video) return;

      this.setupPreloadObserver();
      this.media?.addEventListener('pointerenter', this.onMediaPointerEnter);

      if (this.classList.contains('social-videos-item--has-play')) {
        this.playButton?.addEventListener('click', this.onPlayClick);
        this.video.addEventListener('click', this.onVideoClick);
        this.video.addEventListener('playing', this.onVideoPlaying);
        this.video.addEventListener('pause', this.onVideoPause);
        this.video.addEventListener('ended', this.onVideoEnded);
        this.updatePlayingState();
        return;
      }

      this.media?.addEventListener('click', this.onMediaClick);
      this.video.addEventListener('click', this.onVideoClick);
      this.video.addEventListener('playing', this.onVideoPlaying);
      this.video.addEventListener('pause', this.onVideoPause);
      this.video.addEventListener('ended', this.onVideoEnded);
      this.updatePlayingState();
    }

    disconnectedCallback() {
      this.preloadObserver?.disconnect();
      this.playButton?.removeEventListener('click', this.onPlayClick);
      this.video?.removeEventListener('click', this.onVideoClick);
      this.video?.removeEventListener('playing', this.onVideoPlaying);
      this.video?.removeEventListener('pause', this.onVideoPause);
      this.video?.removeEventListener('ended', this.onVideoEnded);
      this.media?.removeEventListener('click', this.onMediaClick);
      this.media?.removeEventListener('pointerenter', this.onMediaPointerEnter);
    }

    getSlider() {
      return this.closest('social-videos-slider');
    }

    isSliderDragging() {
      return Boolean(this.getSlider()?.isDragging);
    }

    setupPreloadObserver() {
      if (!('IntersectionObserver' in window)) {
        this.preloadVideo();
        return;
      }

      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isSliderDragging()) {
              this.preloadVideo();
            }
          });
        },
        { rootMargin: '120px 0px', threshold: 0 }
      );

      this.preloadObserver.observe(this);
    }

    onMediaPointerEnter() {
      this.preloadVideo();
    }

    preloadVideo() {
      if (!this.video || this.isVideoLoaded) {
        return this.preloadPromise || Promise.resolve();
      }

      const src = this.video.dataset.videoSrc;

      if (!src) {
        return Promise.resolve();
      }

      this.preloadPromise = new Promise((resolve) => {
        const finishPreload = () => {
          this.video.removeEventListener('loadeddata', finishPreload);
          this.video.removeEventListener('error', finishPreload);
          resolve();
        };

        this.video.addEventListener('loadeddata', finishPreload, { once: true });
        this.video.addEventListener('error', finishPreload, { once: true });

        const source = document.createElement('source');
        source.src = src;

        if (this.video.dataset.videoType) {
          source.type = this.video.dataset.videoType;
        }

        this.video.appendChild(source);
        this.video.preload = 'auto';
        this.isVideoLoaded = true;
        this.video.load();

        if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          finishPreload();
        }
      });

      return this.preloadPromise;
    }

    updatePlayingState() {
      if (!this.video) return;

      this.classList.toggle('is-playing', !this.video.paused);
    }

    suspendPlayback() {
      if (!this.video || this.video.paused) return;

      this.video.pause();
      this.updatePlayingState();
    }

    pausePlayback() {
      if (!this.video) return;

      this.video.pause();
      this.updatePlayingState();

      if (SocialVideosItem.currentPlaying === this.video) {
        SocialVideosItem.currentPlaying = null;
      }
    }

    static pauseAll() {
      if (!SocialVideosItem.currentPlaying) return;

      const item = SocialVideosItem.currentPlaying.closest('social-videos-item');

      SocialVideosItem.currentPlaying.pause();
      SocialVideosItem.currentPlaying = null;
      item?.updatePlayingState?.();
    }

    pauseOtherVideos() {
      if (
        !SocialVideosItem.currentPlaying ||
        SocialVideosItem.currentPlaying === this.video
      ) {
        return;
      }

      SocialVideosItem.pauseAll();
    }

    onMediaClick(event) {
      if (this.isSliderDragging()) return;

      event.preventDefault();
      event.stopPropagation();

      if (!this.video) return;

      if (this.video.paused) {
        this.playWithSound();
        return;
      }

      this.pausePlayback();
    }

    onPlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.playWithSound();
    }

    onVideoClick(event) {
      if (!this.video || this.video.paused || this.isSliderDragging()) return;

      event.preventDefault();
      event.stopPropagation();
      this.pausePlayback();
    }

    onVideoPlaying() {
      this.updatePlayingState();
    }

    onVideoPause() {
      this.updatePlayingState();
    }

    onVideoEnded() {
      this.updatePlayingState();

      if (SocialVideosItem.currentPlaying === this.video) {
        SocialVideosItem.currentPlaying = null;
      }
    }

    async playWithSound() {
      if (!this.video || this.isSliderDragging()) return;

      this.pauseOtherVideos();
      await this.preloadVideo();
      this.video.muted = false;

      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          this.updatePlayingState();
        });
      }

      this.updatePlayingState();
      SocialVideosItem.currentPlaying = this.video;
    }
  }

  SocialVideosItem.currentPlaying = null;

  customElements.define('social-videos-item', SocialVideosItem);
})();
